/**
 * Utilitário para limpar caches do navegador e Service Worker
 * Funciona tanto com PWA quanto sem PWA
 */

export interface CacheCleanupProgress {
  step: string;
  progress: number;
  total: number;
}

export type ProgressCallback = (progress: CacheCleanupProgress) => void;

/**
 * Limpa todos os caches do Service Worker
 */
async function clearServiceWorkerCaches(progressCallback?: ProgressCallback): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  progressCallback?.({
    step: 'Limpando Service Workers...',
    progress: 1,
    total: 5,
  });

  try {
    // Desregistrar todos os Service Workers
    const registrations = await navigator.serviceWorker.getRegistrations();
    
    for (const registration of registrations) {
      await registration.unregister();
    }

    progressCallback?.({
      step: 'Service Workers removidos',
      progress: 2,
      total: 5,
    });
  } catch (error) {
    console.error('Erro ao limpar Service Workers:', error);
  }
}

/**
 * Limpa todos os caches do Cache API
 */
async function clearCacheStorage(progressCallback?: ProgressCallback): Promise<void> {
  if (!('caches' in window)) {
    return;
  }

  progressCallback?.({
    step: 'Limpando Cache Storage...',
    progress: 3,
    total: 5,
  });

  try {
    const cacheNames = await caches.keys();
    
    await Promise.all(
      cacheNames.map((cacheName) => {
        return caches.delete(cacheName);
      })
    );

    progressCallback?.({
      step: 'Cache Storage limpo',
      progress: 4,
      total: 5,
    });
  } catch (error) {
    console.error('Erro ao limpar Cache Storage:', error);
  }
}

/**
 * Limpa dados do localStorage relacionados à aplicação
 */
function clearLocalStorage(progressCallback?: ProgressCallback): void {
  progressCallback?.({
    step: 'Limpando dados locais...',
    progress: 4,
    total: 5,
  });

  try {
    // Limpar apenas dados relacionados à aplicação
    // Manter dados importantes como tokens de autenticação
    const keysToKeep = ['auth_token', 'refresh_token', 'user_preferences'];
    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keysToKeep.includes(key)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });

    progressCallback?.({
      step: 'Dados locais limpos',
      progress: 5,
      total: 5,
    });
  } catch (error) {
    console.error('Erro ao limpar localStorage:', error);
  }
}

/**
 * Limpa dados do sessionStorage
 */
function clearSessionStorage(progressCallback?: ProgressCallback): void {
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Erro ao limpar sessionStorage:', error);
  }
}

/**
 * Força o navegador a buscar recursos do servidor (bypass cache)
 */
function forceReload(): void {
  // Adicionar timestamp aos recursos para forçar reload
  const timestamp = Date.now();
  
  // Limpar cache do navegador usando reload com bypass
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    // Se houver service worker, ele será atualizado
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
  }
}

/**
 * Limpa todos os caches e dados relacionados à aplicação
 * @param progressCallback Callback para reportar progresso
 * @returns Promise que resolve quando a limpeza estiver completa
 */
export async function clearAllCaches(
  progressCallback?: ProgressCallback
): Promise<void> {
  try {
    progressCallback?.({
      step: 'Iniciando limpeza...',
      progress: 0,
      total: 5,
    });

    // 1. Limpar Service Workers
    await clearServiceWorkerCaches(progressCallback);

    // 2. Limpar Cache Storage
    await clearCacheStorage(progressCallback);

    // 3. Limpar localStorage (mantendo dados importantes)
    clearLocalStorage(progressCallback);

    // 4. Limpar sessionStorage
    clearSessionStorage(progressCallback);

    // 5. Forçar reload
    progressCallback?.({
      step: 'Finalizando...',
      progress: 5,
      total: 5,
    });

    // Pequeno delay para garantir que tudo foi limpo
    await new Promise((resolve) => setTimeout(resolve, 500));

    progressCallback?.({
      step: 'Limpeza concluída!',
      progress: 5,
      total: 5,
    });
  } catch (error) {
    console.error('Erro durante limpeza de cache:', error);
    throw error;
  }
}

/**
 * Verifica se há atualização disponível comparando versões
 */
export function checkVersionUpdate(): boolean {
  const currentVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
  const storedVersion = localStorage.getItem('app-version');
  const processedVersion = localStorage.getItem('app-version-processed');
  
  if (!currentVersion) {
    return false;
  }

  // Se a versão atual já foi processada, NUNCA mostrar atualização
  if (processedVersion === currentVersion) {
    return false;
  }

  if (!storedVersion) {
    // Primeira vez - salvar versão atual
    localStorage.setItem('app-version', currentVersion);
    return false;
  }

  const hasUpdate = currentVersion !== storedVersion;
  
  if (hasUpdate) {
    const lastNotifiedVersion = localStorage.getItem('app-version-last-notified');
    
    if (lastNotifiedVersion === currentVersion) {
      return false;
    }
    
    localStorage.setItem('app-version-last-notified', currentVersion);
  }
  
  return hasUpdate;
}

/**
 * Função auxiliar para verificar versão (evita conflito de nomes)
 */
export function hasVersionUpdate(): boolean {
  return checkVersionUpdate();
}

/**
 * Atualiza a versão armazenada
 */
export function updateStoredVersion(): void {
  const currentVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content');
  if (currentVersion) {
    localStorage.setItem('app-version', currentVersion);
    localStorage.setItem('app-version-updated', Date.now().toString());
    localStorage.setItem('app-version-processed', currentVersion);
    localStorage.removeItem('app-version-last-notified');
  }
}
